import React, { useState, useRef, useEffect, useMemo } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import axios from 'axios';
import { url } from '../../../../../env';
import { pdf } from '@react-pdf/renderer';
import PurchaseOrderPDF from '../PurchaseOrderPDF';
import { InvoiceComparisonPanel } from './InvoiceComparisonPanel';
import { useQuoteAdjustmentDraft } from '../../../../../Hooks/useQuoteAdjustmentDraft';
import { AppTextField, AppTextareaField } from '../../../../../Components/FormKit';

const cardHeader = (title) => (
    <div style={{
        background: '#f8fafc',
        padding: '0.65rem 1rem',
        borderBottom: '1px solid #e2e8f0',
        borderRadius: '14px 14px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    }}>
        <span style={{ fontWeight: 700, fontSize: '0.72rem', color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
            {title}
        </span>
        <div style={{ flex: 1, height: '1px', background: '#bfdbfe' }} />
    </div>
);

export const RequestInvoiceModal = ({ visible, onHide, persona, stepStatus, stepData, onSuccess, toast, onOpenRecotizacion, onGoToApproval, onDraftDiscarded, onFileDeleted }) => {
    const isCompleted = stepStatus === 'completed';
    const [sending, setSending] = useState(false);
    const [uploadingInvoice, setUploadingInvoice] = useState(false); // State for XML upload
    // Initial Data
    const vehicleModel = persona?.infoBudget?.vehicle?.model || 'Modelo';
    const vehicleBrand = persona?.infoBudget?.vehicle?.brand || 'Marca';
    const vehicleYear = persona?.infoBudget?.vehicle?.year || '2025';
    const totalAmount = persona?.infoBudget?.price || 0;
    const formattedAmount = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalAmount);
    // Form States
    const [clientName, setClientName] = useState(persona?.name || 'Cliente');
    const [agencyName, setAgencyName] = useState('');
    const [contactName, setContactName] = useState('');
    const [email, setEmail] = useState('');
    const [ccEmails, setCcEmails] = useState([]);
    const [subject, setSubject] = useState('');
    const [emailContent, setEmailContent] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [generatedPDF, setGeneratedPDF] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);

    const fileInputRef = useRef(null);
    const invoiceInputRef = useRef(null);
    const [invoiceFiles, setInvoiceFiles] = useState([]); // Track selected invoice files
    const [localProcessedFiles, setLocalProcessedFiles] = useState([]); // Track uploaded files for immediate display
    const [deletingFileId, setDeletingFileId] = useState(null); // Track which file is being deleted
    const [deletedFileIds, setDeletedFileIds] = useState([]); // Track deleted files to hide them from UI

    // Draft detection per file (keyed by source_invoice_file_id)
    const { getDraftsByBudget, discardDraft } = useQuoteAdjustmentDraft();
    const [draftsByFileId, setDraftsByFileId] = useState({});
    const [checkingActiveDraft, setCheckingActiveDraft] = useState(false);

    useEffect(() => {
        if (visible && persona?.infoBudget?.budget_id) {
            setCheckingActiveDraft(true);
            getDraftsByBudget(persona.infoBudget.budget_id)
                .then(result => {
                    if (result?.success && result.data) {
                        const map = {};
                        for (const d of result.data) {
                            if (!d.source_invoice_file_id) continue;
                            const fid = d.source_invoice_file_id;
                            const existing = map[fid];
                            if (!existing || ['draft', 'ready_for_review'].includes(d.status)) {
                                map[fid] = d;
                            }
                        }
                        setDraftsByFileId(map);
                    } else {
                        setDraftsByFileId({});
                    }
                })
                .catch(() => setDraftsByFileId({}))
                .finally(() => setCheckingActiveDraft(false));
        } else {
            setDraftsByFileId({});
        }
    }, [visible, persona?.infoBudget?.budget_id]);

    // True if ANY draft is in progress (blocks recotizar on all files + blocks delete on its file)
    const hasAnyActiveDraft = useMemo(() =>
        Object.values(draftsByFileId).some(d => ['draft', 'ready_for_review'].includes(d.status)),
        [draftsByFileId]);

    const handleDiscardDraft = async (draft) => {
        if (!window.confirm('¿Descartar el borrador de recotización? Esta acción no se puede deshacer.')) return;
        try {
            await discardDraft(draft.id);
            setDraftsByFileId(prev => {
                const next = { ...prev };
                if (draft.source_invoice_file_id) delete next[draft.source_invoice_file_id];
                return next;
            });
            toast?.current?.show({ severity: 'success', summary: 'Borrador descartado', detail: 'La recotización ha sido descartada', life: 3000 });
            onDraftDiscarded?.();
        } catch (err) {
            toast?.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo descartar el borrador', life: 4000 });
        }
    };

    // Default Email Template
    const generateDefaultContent = (contact = '', client = clientName) => {
        return `Estimado ${contact},

Adjunto te envío la Orden de Compra correspondiente al ${vehicleBrand} ${vehicleModel} ${vehicleYear} para ${client}.

Recuerda que PEICAR se queda con un duplicado de llave y hacemos nosotros los tramites vehiculares, así como la contratación del seguro.

Detalles de la operación:

Monto total: ${formattedAmount} MXN (IVA incluido)
Método de pago: PUE (Pago en una sola exhibición)
Factura a nombre de: Conduce Capital SAPI de C.V.
RFC: CCA2308224G3
Condiciones de pago: Transferencia bancaria con un plazo máximo de 30 días a partir de la recepción de la factura.
Entrega de la unidad: En la concesionaria previa autorización de PEICAR.
Llaves: Se entregará solo una llave al cliente; la otra deberá ser entregada a la arrendadora.

Por favor, confirma la recepción del documento y avísame cualquier duda o requerimiento adicional.

Quedo atento a tu respuesta.

Saludos cordiales,
Equipo Peicar`;
    };

    // Generate PDF Effect
    useEffect(() => {
        if (visible && persona) {
            const generatePDF = async () => {
                try {
                    const blob = await pdf(
                        <PurchaseOrderPDF
                            persona={persona}
                            contactName={contactName}
                            agencyName={agencyName}
                            date={new Date()}
                        />
                    ).toBlob();

                    const fileName = `Orden de compra ${clientName || 'Cliente'}.pdf`;
                    const file = new File([blob], fileName, { type: 'application/pdf' });
                    setGeneratedPDF(file);

                    setPdfUrl(prev => {
                        if (prev) URL.revokeObjectURL(prev);
                        return URL.createObjectURL(blob);
                    });
                } catch (error) {
                    console.error("Error creating PDF:", error);
                }
            };

            const timeoutId = setTimeout(generatePDF, 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [visible, persona, contactName, agencyName, clientName]);

    useEffect(() => {
        if (visible) {
            if (isCompleted && stepData?.metadata) {
                const meta = stepData.metadata;
                setAgencyName(meta.agency_name || '');
                setContactName(meta.contact_name || '');
                setEmail(meta.email_sent_to || '');
                setCcEmails(meta.cc || []);
                setSubject(meta.subject || '');
                setEmailContent(meta.content || '');
                setClientName(meta.client_name || persona?.name || 'Cliente');
            } else {
                setAgencyName('');
                setContactName('');
                setEmail('');
                setCcEmails([]);
                setClientName(persona?.name || 'Cliente');
                const initialClientName = persona?.name || 'Cliente';
                setSubject(`Envío de Orden de Compra – ${vehicleBrand} ${vehicleModel} ${vehicleYear} | ${initialClientName}`);
                setEmailContent(generateDefaultContent('', initialClientName));
            }
        }
    }, [visible, isCompleted, stepData, persona]);

    // Update content and subject when contact name or client name changes (only if not completed)
    useEffect(() => {
        if (!isCompleted) {
            setEmailContent(generateDefaultContent(contactName, clientName));
            setSubject(`Envío de Orden de Compra – ${vehicleBrand} ${vehicleModel} ${vehicleYear} | ${clientName}`);
        }
    }, [contactName, clientName, isCompleted]);

    // Handlers
    const addCcEmail = () => setCcEmails([...ccEmails, '']);
    const updateCcEmail = (index, value) => {
        const newCc = [...ccEmails];
        newCc[index] = value;
        setCcEmails(newCc);
    };
    const removeCcEmail = (index) => setCcEmails(ccEmails.filter((_, i) => i !== index));

    // Memoized file list to track count and duplicates
    const allInvoiceFiles = useMemo(() => {
        const serverFiles = (stepData?.files?.filter(f => f.file_type === 'invoice_xml') || []).map(f => ({
            ...f,
            analysis: f.metadata?.analysis || null,
            comparison: f.metadata?.comparison || null,
        }));
        const combined = [...serverFiles];

        localProcessedFiles.forEach(lf => {
            if (!combined.find(f => f.id === lf.file_id || f.id === lf.id)) {
                combined.push({
                    id: lf.file_id || lf.id,
                    file_id: lf.file_id,
                    file_name: lf.file_name,
                    s3_url: lf.s3_url,
                    created_at: new Date().toISOString(),
                    file_type: 'invoice_xml',
                    analysis: lf.analysis || null,
                    comparison: lf.comparison || null,
                });
            }
        });
        return combined.filter(f => !deletedFileIds.includes(f.id || f.file_id));
    }, [stepData, localProcessedFiles, deletedFileIds]);

    // Compact file handlers — Orden de Compra adjuntos
    const handleFileSelect = (e) => {
        const added = Array.from(e.target.files || []);
        setUploadedFiles([...uploadedFiles, ...added]);
        e.target.value = '';
    };
    const removeUploadedFile = (index) => {
        setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
    };

    // Compact file handlers — XML invoices selection
    const handleInvoiceSelect = (e) => {
        const added = Array.from(e.target.files || []);
        setInvoiceFiles([...invoiceFiles, ...added]);
        e.target.value = '';
    };
    const removeInvoiceFile = (index) => {
        setInvoiceFiles(invoiceFiles.filter((_, i) => i !== index));
    };

    // XML Upload Handler — now accepts a files array directly
    const handleInvoiceUpload = async () => {
        const files = invoiceFiles;
        if (!files || files.length === 0) return;

        const maxFiles = 3;
        const currentCount = allInvoiceFiles.length;
        const remainingSlots = maxFiles - currentCount;

        if (remainingSlots <= 0) {
            toast.current.show({ severity: 'warn', summary: 'Límite alcanzado', detail: `Ya has subido el máximo de ${maxFiles} archivos.` });
            setInvoiceFiles([]);
            return;
        }

        let filesToUpload = files;
        if (files.length > remainingSlots) {
            toast.current.show({ severity: 'info', summary: 'Ajuste de Carga', detail: `Solo se subirán ${remainingSlots} archivo(s) para no exceder el límite de 3.` });
            filesToUpload = files.slice(0, remainingSlots);
        }

        const acceptedTypes = ['text/xml', 'application/xml', 'application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

        const validFiles = filesToUpload.filter(file => {
            const isXml = file.name.toLowerCase().endsWith('.xml');
            return isXml || acceptedTypes.includes(file.type);
        });

        if (validFiles.length !== files.length) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Algunos archivos no eran válidos y fueron ignorados.' });
        }

        if (validFiles.length === 0) {
            setInvoiceFiles([]);
            return;
        }

        setUploadingInvoice(true);
        let errorOccurred = false;

        const uploadPromises = validFiles.map(async (file) => {
            try {
                const formData = new FormData();
                if (stepData?.id) {
                    formData.append('step_id', stepData.id);
                } else if (persona?.infoBudget?.budget_id) {
                    formData.append('budget_id', persona.infoBudget.budget_id);
                }
                formData.append('file', file);
                formData.append('uploaded_by_type', 'admin');
                formData.append('type_persona', persona?.typePerson || 'PF');

                const response = await axios.post(`${url}/quote-process/upload-invoice-xml`, formData, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.data.success) {
                    return response.data;
                } else {
                    throw new Error(response.data.message || 'Error desconocido');
                }
            } catch (e) {
                console.error("Upload failed for file", file.name, e);
                errorOccurred = true;
                return null;
            }
        });

        const results = await Promise.all(uploadPromises);
        const successfulUploads = results.filter(r => r !== null);

        setUploadingInvoice(false);

        if (successfulUploads.length > 0) {
            toast.current.show({
                severity: 'success',
                summary: 'Éxito',
                detail: `${successfulUploads.length} archivo(s) subido(s) correctamente`
            });

            const newFiles = successfulUploads.map(r => r.data).filter(f => f);
            setLocalProcessedFiles(prev => [...prev, ...newFiles]);

            setInvoiceFiles([]);

            onFileDeleted?.();
        }

        if (errorOccurred) {
            if (successfulUploads.length === 0) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron subir los archivos.' });
            } else {
                toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Algunos archivos fallaron al subir.' });
            }
        }
    };

    const handleDeleteInvoice = async (fileId) => {
        const fileDraft = draftsByFileId[fileId];
        if (fileDraft && ['draft', 'ready_for_review'].includes(fileDraft.status)) {
            toast?.current?.show({ severity: 'warn', summary: 'No se puede eliminar', detail: 'Esta factura tiene una recotización en proceso. Descarta el borrador primero.', life: 4000 });
            return;
        }
        if (!window.confirm('¿Estás seguro de que deseas eliminar esta factura?')) return;

        setDeletingFileId(fileId);
        try {
            const response = await axios.delete(`${url}/quote-process/invoice-file/${fileId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.data.success) {
                toast.current.show({ severity: 'success', summary: 'Eliminada', detail: 'Factura eliminada correctamente' });
                setLocalProcessedFiles(prev => prev.filter(f => (f.file_id || f.id) !== fileId));
                setDeletedFileIds(prev => [...prev, fileId]);
                onFileDeleted?.();
            }
        } catch (e) {
            console.error('Error deleting invoice:', e);
            const detail = e.response?.data?.message || 'No se pudo eliminar la factura.';
            toast.current.show({ severity: 'error', summary: 'Error', detail });
        } finally {
            setDeletingFileId(null);
        }
    };

    const validate = () => {
        if (!email) return 'El correo de la agencia es requerido.';
        if (!contactName) return 'El nombre de contacto es requerido.';
        if (!clientName) return 'El nombre del cliente es requerido.';
        if (!subject) return 'El asunto es requerido.';
        if (!emailContent) return 'El contenido es requerido.';
        return null;
    };

    const handleSend = async () => {
        const error = validate();
        if (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error });
            return;
        }

        setSending(true);
        try {
            const formData = new FormData();
            formData.append('email', email);
            formData.append('agency_name', agencyName);
            formData.append('contact_name', contactName);
            formData.append('client_name', clientName);
            formData.append('subject', subject);
            formData.append('content', emailContent);
            formData.append('person_id', persona.id);
            formData.append('step_type', 'request_invoice');
            formData.append('type_persona', persona?.typePerson || 'PF');

            ccEmails.filter(c => c).forEach((c, i) => formData.append(`cc[${i}]`, c));

            uploadedFiles.forEach((f, i) => formData.append(`attachments[${i}]`, f));

            if (generatedPDF) {
                formData.append(`attachments[${uploadedFiles.length}]`, generatedPDF);
            }

            const token = localStorage.getItem('token');
            const response = await axios.post(`${url}/request-invoice`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                toast.current.show({ severity: 'success', summary: 'Enviado', detail: 'Orden de compra enviada correctamente.' });
                if (onSuccess) onSuccess(response.data.data);
                onHide();
            }
        } catch (e) {
            console.error(e);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo enviar el correo.' });
        } finally {
            setSending(false);
        }
    };

    const requestFilesHistory = (stepData?.files || []).filter(f => f.file_type !== 'invoice_xml');

    const renderRequestForm = () => (
        <div className="fk-root flex flex-column gap-3">

            {/* Card 1: Datos de la Agencia y Cliente */}
            <div className="tk-table-card">
                {cardHeader('Datos de la Agencia y Cliente')}
                <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <AppTextField
                                label="Nombre Completo del Cliente"
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                                placeholder="Nombre del cliente"
                                disabled={isCompleted}
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <AppTextField
                                label="Nombre de la Agencia"
                                value={agencyName}
                                onChange={(e) => setAgencyName(e.target.value)}
                                placeholder="Nombre de la agencia"
                                disabled={isCompleted}
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <AppTextField
                                label="Nombre del Contacto"
                                value={contactName}
                                onChange={(e) => setContactName(e.target.value)}
                                placeholder="Contacto en la agencia"
                                disabled={isCompleted}
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <AppTextField
                                label="Correo de la Agencia"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="agencia@ejemplo.com"
                                disabled={isCompleted}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Card 2: Destinatario y Copias */}
            <div className="tk-table-card">
                {cardHeader('Destinatario y Copias')}
                <div style={{ padding: '1rem' }} className="flex flex-column gap-2">
                    <div className="flex align-items-center justify-content-between">
                        <span className="fk-label" style={{ margin: 0 }}>CC — Con Copia</span>
                        {!isCompleted && (
                            <button
                                type="button"
                                onClick={addCcEmail}
                                className="fk-btn fk-btn--secondary fk-btn--sm"
                                style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            >
                                <i className="pi pi-plus" style={{ fontSize: '0.7rem' }} />
                                Agregar CC
                            </button>
                        )}
                    </div>
                    {ccEmails.map((cc, i) => (
                        <div key={i} className="flex gap-2 align-items-center">
                            <div className="fk-input flex-1">
                                <input
                                    className="p-inputtext"
                                    value={cc}
                                    onChange={(e) => updateCcEmail(i, e.target.value)}
                                    placeholder="copia@ejemplo.com"
                                    disabled={isCompleted}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            {!isCompleted && (
                                <button
                                    type="button"
                                    className="fk-icon-btn fk-icon-btn--danger"
                                    onClick={() => removeCcEmail(i)}
                                    title="Eliminar"
                                >
                                    <i className="pi pi-times" style={{ fontSize: '0.8rem' }} />
                                </button>
                            )}
                        </div>
                    ))}
                    {ccEmails.length === 0 && (
                        <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                            No hay copias agregadas
                        </span>
                    )}
                </div>
            </div>

            {/* Card 3: Configuración del Correo */}
            <div className="tk-table-card">
                {cardHeader('Configuración del Correo')}
                <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                    <AppTextField
                        label="Asunto"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Asunto del correo"
                        disabled={isCompleted}
                    />
                    <AppTextareaField
                        label="Contenido del correo"
                        value={emailContent}
                        onChange={(e) => setEmailContent(e.target.value)}
                        rows={12}
                        placeholder="Escribe el mensaje aquí..."
                        readOnly={isCompleted}
                    />
                </div>
            </div>

            {/* Card 4: Orden de Compra y Adjuntos */}
            <div className="tk-table-card">
                {cardHeader('Orden de Compra y Adjuntos')}
                <div style={{ padding: '1rem' }} className="flex flex-column gap-3">

                    {/* PDF de Orden de Compra Generada */}
                    {generatedPDF && (
                        <div className="flex align-items-center gap-2 p-2 border-1 border-gray-200 border-round bg-white shadow-1" style={{ maxWidth: '100%' }}>
                            <i className="pi pi-file-pdf text-red-500 text-xl"></i>
                            <div className="flex flex-column" style={{ flex: 1, minWidth: 0 }}>
                                <span className="text-sm font-medium text-gray-800 white-space-nowrap overflow-hidden text-overflow-ellipsis" title={generatedPDF.name}>{generatedPDF.name}</span>
                                <span className="text-xs text-gray-500">Orden de compra generada automáticamente</span>
                            </div>
                            {pdfUrl && (
                                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="ml-auto flex align-items-center p-1 hover:bg-gray-100 border-round">
                                    <i className="pi pi-eye text-blue-600 cursor-pointer" title="Ver PDF"></i>
                                </a>
                            )}
                        </div>
                    )}

                    {/* Adjuntar nuevos */}
                    {!isCompleted && (
                        <>
                            {generatedPDF && <div style={{ height: '1px', background: '#e2e8f0' }} />}
                            <div className="flex align-items-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    className="fk-btn fk-btn--secondary fk-btn--sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}
                                >
                                    <i className="pi pi-paperclip" style={{ fontSize: '0.72rem' }} />
                                    Adjuntar documentos
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept="application/pdf,image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                                {uploadedFiles.length === 0 && (
                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Sin archivos adicionales</span>
                                )}
                                {uploadedFiles.map((file, i) => (
                                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '99px', padding: '0.2rem 0.4rem 0.2rem 0.65rem', fontSize: '0.73rem', color: '#334155', maxWidth: '220px' }}>
                                        <i className="pi pi-file" style={{ fontSize: '0.68rem', flexShrink: 0 }} />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                        <span style={{ color: '#94a3b8', flexShrink: 0 }}>({(file.size / 1024).toFixed(0)} KB)</span>
                                        <button type="button" onClick={() => removeUploadedFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 0.1rem', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                            <i className="pi pi-times" style={{ fontSize: '0.62rem' }} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Historial: archivos enviados */}
                    {requestFilesHistory.length > 0 && (
                        <>
                            <div style={{ height: '1px', background: '#e2e8f0' }} />
                            <div>
                                <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.25rem' }}>
                                    Archivos Cargados (Solicitud)
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {requestFilesHistory.map((file, index) => (
                                        <div key={index} className="flex align-items-center gap-2 p-2 border-1 border-gray-300 border-round bg-white shadow-1" style={{ minWidth: '200px' }}>
                                            <i className={`pi ${file.file_name?.endsWith('.pdf') ? 'pi-file-pdf text-red-500' : 'pi-image text-blue-500'} text-xl`}></i>
                                            <div className="flex flex-column" style={{ maxWidth: '160px' }}>
                                                <span className="text-sm font-medium text-gray-800 white-space-nowrap overflow-hidden text-overflow-ellipsis" title={file.file_name}>{file.file_name}</span>
                                                <span className="text-xs text-gray-500">{new Date(file.created_at).toLocaleDateString()}</span>
                                            </div>
                                            {file.s3_url && (
                                                <a href={file.s3_url} target="_blank" rel="noopener noreferrer" className="ml-auto flex align-items-center p-1 hover:bg-gray-100 border-round">
                                                    <i className="pi pi-eye text-blue-600 cursor-pointer"></i>
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-content-end gap-2 mt-2 pt-3 border-top-1 border-gray-200">
                <Button label={isCompleted ? "Cerrar" : "Cancelar"} icon="pi pi-times" onClick={onHide} className="p-button-outlined p-button-secondary" />
                {!isCompleted && (
                    <Button label="Enviar Correo" icon="pi pi-send" onClick={handleSend} loading={sending} className="Button Green" />
                )}
            </div>
        </div>
    );

    const renderInvoiceReceivedTab = () => (
        <div className="fk-root flex flex-column gap-3 p-3">
            {/* Card: Facturas Cargadas */}
            <div className="tk-table-card">
                {cardHeader('Facturas Cargadas')}
                <div style={{ padding: '1rem' }}>
                    {allInvoiceFiles.length > 0 ? (
                        <div className="flex flex-column gap-3">
                            {allInvoiceFiles.map((file, idx) => (
                                <div key={file.id || idx}>
                                    <div className="flex align-items-center justify-content-between p-3 border-1 border-gray-200 border-round bg-white">
                                        <div className="flex align-items-center gap-3">
                                            <i className="pi pi-file-excel text-green-500 text-2xl"></i>
                                            <div>
                                                <span className="font-semibold block text-gray-800">{file.file_name}</span>
                                                <span className="text-sm text-gray-500">Subido el {new Date(file.created_at).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                        <div className="flex align-items-center gap-1">
                                            {file.s3_url && (
                                                <a href={file.s3_url} target="_blank" rel="noopener noreferrer" className="p-button p-button-text p-button-rounded p-button-sm">
                                                    <i className="pi pi-download text-lg"></i>
                                                </a>
                                            )}
                                            <Button
                                                icon={deletingFileId === (file.id || file.file_id) ? "pi pi-spin pi-spinner" : "pi pi-trash"}
                                                className="p-button-text p-button-rounded p-button-danger p-button-sm"
                                                onClick={() => handleDeleteInvoice(file.id || file.file_id)}
                                                disabled={deletingFileId !== null || !!draftsByFileId[file.id]?.status && ['draft', 'ready_for_review'].includes(draftsByFileId[file.id]?.status)}
                                                tooltip={draftsByFileId[file.id] && ['draft', 'ready_for_review'].includes(draftsByFileId[file.id]?.status) ? "No se puede eliminar: tiene recotización en proceso" : "Eliminar factura"}
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        </div>
                                    </div>
                                    <InvoiceComparisonPanel
                                        fileData={file}
                                        budgetData={persona?.infoBudget}
                                        activeAdjustmentDraft={draftsByFileId[file.id] || null}
                                        hasAnyActiveDraft={hasAnyActiveDraft}
                                        onRecotizarClick={(context) => {
                                            if (onOpenRecotizacion) {
                                                onOpenRecotizacion(context);
                                                onHide();
                                            }
                                        }}
                                        onGoToApproval={() => {
                                            onHide();
                                            onGoToApproval?.();
                                        }}
                                        onDiscardDraft={handleDiscardDraft}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>No se han cargado facturas XML aún.</span>
                    )}
                </div>
            </div>

            {/* Card: Subir Nueva Factura */}
            <div className="tk-table-card">
                {cardHeader('Subir Nueva Factura (XML)')}
                <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                    {allInvoiceFiles.length < 3 ? (
                        <>
                            <div className="flex align-items-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    className="fk-btn fk-btn--secondary fk-btn--sm"
                                    onClick={() => invoiceInputRef.current?.click()}
                                    style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}
                                    disabled={uploadingInvoice}
                                >
                                    <i className="pi pi-paperclip" style={{ fontSize: '0.72rem' }} />
                                    Seleccionar Factura (XML)
                                </button>
                                <input
                                    ref={invoiceInputRef}
                                    type="file"
                                    multiple
                                    accept=".xml,.pdf,image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleInvoiceSelect}
                                />
                                {invoiceFiles.length === 0 && (
                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Sin archivos seleccionados</span>
                                )}
                                {invoiceFiles.map((file, i) => (
                                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '99px', padding: '0.2rem 0.4rem 0.2rem 0.65rem', fontSize: '0.73rem', color: '#334155', maxWidth: '220px' }}>
                                        <i className="pi pi-file" style={{ fontSize: '0.68rem', flexShrink: 0 }} />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                        <span style={{ color: '#94a3b8', flexShrink: 0 }}>({(file.size / 1024).toFixed(0)} KB)</span>
                                        <button type="button" onClick={() => removeInvoiceFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 0.1rem', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                            <i className="pi pi-times" style={{ fontSize: '0.62rem' }} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            {invoiceFiles.length > 0 && (
                                <div className="flex justify-content-end">
                                    <Button
                                        label="Subir Facturas"
                                        icon="pi pi-upload"
                                        className="Button Green"
                                        onClick={handleInvoiceUpload}
                                        loading={uploadingInvoice}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="p-3 border-1 border-yellow-200 bg-yellow-50 text-yellow-800 border-round">
                            <i className="pi pi-info-circle mr-2"></i>
                            Has alcanzado el límite de 3 facturas cargadas.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <Dialog
            header={
                <div className="flex align-items-center gap-2">
                    <h2
                        style={{
                            background: "linear-gradient(90deg, #21588C 6.22%, #21588C 77.34%)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            margin: 0
                        }}
                    >
                        {isCompleted ? "Orden de Compra Enviada" : "Enviar Orden de Compra"}
                    </h2>
                </div>
            }
            visible={visible}
            onHide={onHide}
            style={{ width: '50vw', maxWidth: '900px', minWidth: '600px' }}
            breakpoints={{ '960px': '90vw', '641px': '95vw' }}
            contentStyle={{ maxHeight: '75vh', overflowY: 'auto', overscrollBehavior: 'contain' }}
            modal
            draggable={false}
        >
            <style>
                {`
                    /* Modal redondeado para coincidir con las tk-table-card */
                    .p-dialog {
                        background: #ffffff !important;
                        overflow: hidden !important;
                        border-radius: 16px !important;
                    }
                    .p-dialog-header {
                        background: #ffffff !important;
                        margin-bottom: 0 !important;
                        border-top-left-radius: 16px !important;
                        border-top-right-radius: 16px !important;
                    }
                    .p-dialog-content {
                        background: #ffffff !important;
                        margin-top: 0 !important;
                        border-bottom-left-radius: 16px !important;
                        border-bottom-right-radius: 16px !important;
                    }

                    /* Custom TabView Styles */
                    .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
                        color: #23b56e !important;
                        border-color: #23b56e !important;
                    }

                    .p-tabview .p-tabview-nav li .p-tabview-nav-link:focus {
                        box-shadow: 0 0 0 0.2rem #a5f3d1 !important;
                    }

                    .p-tabview .p-tabview-nav .p-tabview-ink-bar {
                        background-color: #23b56e !important;
                    }
                `}
            </style>
            <div className="email-modal-content">
                {isCompleted ? (
                    <TabView>
                        <TabPanel header="Información de Solicitud" className='tabPanel'>
                            {renderRequestForm()}
                        </TabPanel>
                        <TabPanel header="Factura Recibida" className='tabPanel'>
                            {renderInvoiceReceivedTab()}
                        </TabPanel>
                    </TabView>
                ) : (
                    renderRequestForm()
                )}
            </div>
        </Dialog>
    );
};
